import { useState } from 'react';
import { IconArrowLeft, IconBell, IconDots, IconPlus, IconSend } from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { useT } from '@hana/i18n';
import { HeaderTool, Page, PageHeader } from '@hana/shell';
import { Button, Drawer, Modal, Segmented, Skeleton, StateView, Textarea, useToast, type ScreenState } from '@hana/ui';
import {
  COMPS, CYCLES, CYCLE_TYPES, FRAME, GAPS, LEVELS, OBJECTIVES, PEOPLE_FILTERS, POSITIONS, PROG_LABEL, SELF_SCORES,
  countNotDone, cyclePeople, cycleStepView, krProgress, matchFilter, matchLevel, mgrScores, nextLevel, objectiveAvg, reviewTotal,
  type Cycle, type Objective, type PeopleFilter, type Person,
} from './review';
import './review.i18n';
import s from './review.module.css';

// Mint-HR-Review (design-specs/Mint-HR-Review.md): 3 tab · Kỳ đánh giá · Mục tiêu OKR · Khung năng lực. Chỉ đọc + thao tác giả lập trong bộ nhớ.
// Không tiền / lương. Số KPI lấy từ dữ liệu mẫu của Mint-KPI.

type Tab = 'cyc' | 'okr' | 'comp';
const TONE: Record<string, string> = { ok: s.tOk ?? '', warn: s.tWarn ?? '', err: s.tErr ?? '', pri: s.tPri ?? '', mute: s.tMute ?? '' };

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}
const initials = (u: string) => u.slice(0, 2).toUpperCase();

function Pill({ on, onClick, children, big }: { on: boolean; onClick: () => void; children: string; big?: boolean }) {
  return <button type="button" aria-pressed={on} className={`${s.pill} ${big ? s.pillBig : ''} ${on ? s.pillOn : ''}`} onClick={onClick}>{children}</button>;
}

export function HrReview() {
  const t = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const forced = readState();
  const [tab, setTab] = useState<Tab>('cyc');
  const [dense, setDense] = useState<'std' | 'compact'>('std');
  const [more, setMore] = useState(false);
  const [added, setAdded] = useState<Cycle[]>([]);
  const cycles = [...added, ...CYCLES];
  const [cy, setCy] = useState(cycles[0]!.id);
  const cx = cycles.find((c) => c.id === cy) ?? cycles[0]!;
  const [rmd, setRmd] = useState({ d7: true, d3: true, d1: true, hei: true, mail: true });
  const [pf, setPf] = useState<PeopleFilter>('all');
  const [saved, setSaved] = useState<Record<string, boolean>>({});
  const [rvUser, setRvUser] = useState<string | null>(null);
  const [rvSc, setRvSc] = useState<Record<number, number>>({});
  const [rvNote, setRvNote] = useState('');
  const [okLv, setOkLv] = useState('all');
  const [krOv, setKrOv] = useState<Record<string, number>>({});
  const [okAdded, setOkAdded] = useState<Objective[]>([]);
  const [pos, setPos] = useState<string>(POSITIONS[0]);
  const [cfEdit, setCfEdit] = useState(false);
  const [cfOv, setCfOv] = useState<Record<string, number>>({});
  const [nf, setNf] = useState<{ kind: 'cyc' | 'okr'; name: string; ty: string; lv: string; who: number; tried: boolean } | null>(null);

  const say = (msg: string, undo?: () => void) => toast({ msg, ...(undo ? { undo } : {}) });
  const objs = [...OBJECTIVES, ...okAdded].filter((o) => matchLevel(o, okLv));
  const people = cyclePeople(cx, saved);
  const notDone = countNotDone(people);
  const open = cycles.filter((c) => c.status === 'open').length;
  const rvP: Person | undefined = rvUser ? people.find((p) => p.user === rvUser) : undefined;

  const state: ScreenState = forced ?? 'ready';
  const kpis: [string, string, string, boolean?][] = [
    [t('hrv.kOpen'), String(open), t('hrv.kOpenU')],
    [t('hrv.kSelf'), '21/36', t('hrv.kSelfU')],
    [t('hrv.kMgr'), '9/36', t('hrv.kMgrU'), true],
    [t('hrv.kOkr'), `${objs.length} O · ${objs.reduce((a, o) => a + o.krs.length, 0)} KR`, t('hrv.kAvg', { p: Math.round(objs.reduce((a, o) => a + objectiveAvg(o, krOv), 0) / Math.max(1, objs.length)) })],
  ];
  const stLabel = (c: Cycle) => (c.status === 'open' ? t('hrv.stOpen') : c.status === 'draft' ? t('hrv.stDraft') : t('hrv.stClosed'));
  const stTone = (c: Cycle) => (c.status === 'open' ? 'pri' : c.status === 'draft' ? 'warn' : 'mute');
  const progLabel = (p: number) => t(`hrv.p${p}`);
  void PROG_LABEL;

  const primary =
    tab === 'cyc' ? { k: t('hrv.pCyc'), act: () => setNf({ kind: 'cyc', name: '', ty: 'Định kỳ năm', lv: 'Phòng HR', who: 0, tried: false }) }
      : tab === 'okr' ? { k: t('hrv.pOkr'), act: () => setNf({ kind: 'okr', name: '', ty: '', lv: 'Phòng HR', who: 0, tried: false }) }
        : cfEdit ? { k: t('hrv.pSave'), act: () => { setCfEdit(false); say(t('hrv.tFrame', { p: pos })); } }
          : { k: t('hrv.pEdit'), act: () => setCfEdit(true) };

  const remindOne = (p: Person) => say(t('hrv.tRemindOne', { w: p.self < 2 ? t('hrv.tRemindSelf', { u: p.user }) : t('hrv.tRemindMgr', { m: p.mgr, u: p.user }) }));
  const remindAll = () => say(t('hrv.tRemindAll', { n: notDone, via: [rmd.hei ? 'Hei' : '', rmd.mail ? 'mail' : ''].filter(Boolean).join(' + ') }));

  const rv = rvP ? (() => {
    const sc = mgrScores(rvP, rvSc);
    const r = reviewTotal(sc, rvP.kpi);
    return { sc, ...r };
  })() : null;
  const closeRv = () => { setRvUser(null); setRvSc({}); };
  const sendRv = () => {
    if (!rvP || !rv) return;
    if (!rv.done) { say(t('hrv.tFull')); return; }
    const prev = saved;
    setSaved({ ...saved, [`${cx.id}:${rvP.user}`]: true });
    closeRv();
    say(t('hrv.tSent', { u: rvP.user, g: rv.grade }), () => setSaved(prev));
  };
  const submitNf = () => {
    if (!nf) return;
    if (!nf.name.trim()) { setNf({ ...nf, tried: true }); return; }
    if (nf.kind === 'cyc') {
      const id = `N${added.length + 1}`;
      const prev = added;
      setAdded([{ id, name: nf.name, type: nf.ty, range: t('hrv.draftRange'), status: 'draft', steps: [['Tự đánh giá', 'chưa đặt', 0, 36], ['Quản lý chấm', 'chưa đặt', 0, 36], ['Công bố', '—', 0, 1]] }, ...added]);
      setCy(id);
      say(t('hrv.tCycNew', { n: nf.name }), () => { setAdded(prev); setCy(CYCLES[0]!.id); });
    } else {
      const prev = okAdded;
      setOkAdded([...okAdded, { id: `N${okAdded.length}`, level: nf.lv, name: nf.name, owner: '[HRD-MGR] HANHLTM', krs: [] }]);
      say(t('hrv.tOkrNew', { n: nf.name }), () => setOkAdded(prev));
    }
    setNf(null);
  };

  const menu: [string, string, () => void][] = [
    [t('hrv.mKpi'), t('hrv.mKpiSub'), () => void navigate({ to: '/mee/hr/kpi' })],
    [t('hrv.mExp'), t('hrv.mExpSub'), () => say(t('hrv.tExp', { n: cx.name }))],
    [t('hrv.mTpl'), t('hrv.mTplSub'), () => say(t('hrv.tTpl'))],
  ];

  const msgState = state === 'empty'
    ? { title: t('hrv.emptyTitle'), desc: t('hrv.emptyDesc'), action: <Button variant="primary" onClick={() => setNf({ kind: 'cyc', name: '', ty: 'Định kỳ năm', lv: 'Phòng HR', who: 0, tried: false })}>{t('hrv.pCyc')}</Button> }
    : state === 'error'
      ? { title: t('hrv.errTitle'), desc: t('hrv.errDesc'), action: <Button onClick={() => say(t('hrv.retrying'))}>{t('hrv.retry')}</Button> }
      : { title: t('hrv.npTitle'), desc: t('hrv.npDesc'), action: <Button onClick={() => void navigate({ to: '/mee' })}>{t('hrv.npBtn')}</Button> };

  return (
    <Page kind="table">
      <PageHeader
        title={t('hrv.title')}
        meta={<span className={s.mute}>{t('hrv.sub')}</span>}
        lead={<HeaderTool label={t('hrv.back')} icon={IconArrowLeft} onClick={() => void navigate({ to: '/mee/hr' })} />}
        actions={
          <>
            <Segmented kind="toggle" label={t('hrv.density')} value={dense} onChange={setDense} items={[{ key: 'std', label: t('hrv.dStd') }, { key: 'compact', label: t('hrv.dCompact') }]} />
            <HeaderTool label={t('hrv.notif')} icon={IconBell} onClick={() => say(t('hrv.tNotif'))} />
            <HeaderTool label={t('hrv.more')} icon={IconDots} active={more} onClick={() => setMore((v) => !v)} />
            {state === 'ready' && <Button variant="primary" icon={tab === 'comp' ? undefined : <IconPlus size={16} aria-hidden="true" />} onClick={primary.act}>{primary.k}</Button>}
          </>
        }
      />
      {more && (
        <div role="menu" aria-label={t('hrv.more')} className={s.menu}>
          {menu.map(([k, sub, fn]) => (
            <button key={k} type="button" role="menuitem" onClick={() => { setMore(false); fn(); }}>{k}<span>{sub}</span></button>
          ))}
        </div>
      )}
      <div className={`${s.page} ${dense === 'compact' ? s.dense : ''}`}>
        <div className={s.tabs}>
          <Segmented label={t('hrv.title')} value={tab} onChange={setTab} items={[{ key: 'cyc', label: t('hrv.tCyc'), n: open }, { key: 'okr', label: t('hrv.tOkr') }, { key: 'comp', label: t('hrv.tComp') }]} />
        </div>
        <StateView state={state} title={msgState.title} desc={msgState.desc} action={msgState.action} skeleton={<Skeleton rows={9} />}>
          <div className={s.kpis}>
            {kpis.map(([k, v, u, warn]) => (
              <div key={k} className={s.kpi}>
                <div className={s.kpiK}>{k}</div>
                <div className={s.kpiV}><b className={warn ? s.warn : undefined}>{v}</b><span>{u}</span></div>
              </div>
            ))}
          </div>

          {tab === 'cyc' && (
            <div className={s.cycGrid}>
              <section className={s.card} aria-label={t('hrv.cycles')} style={{ padding: 10 }}>
                {cycles.map((c) => (
                  <button key={c.id} type="button" aria-current={c.id === cy} className={`${s.cycItem} ${c.id === cy ? s.cycOn : ''}`} onClick={() => { setCy(c.id); setRvUser(null); }}>
                    <span className={s.row}><b>{c.name}</b><span className={`${s.chip} ${TONE[stTone(c)]}`}>{stLabel(c)}</span></span>
                    <span className={s.sub}>{c.type} · {c.range}</span>
                    <span className={s.bar} aria-hidden="true"><span style={{ width: `${c.steps[0]![3] ? Math.round((c.steps[0]![2] / c.steps[0]![3]) * 100) : 0}%` }} /></span>
                  </button>
                ))}
              </section>
              <div className={s.col}>
                <section className={s.card} aria-label={cx.name}>
                  <div className={s.cardHd}><h2>{cx.name}</h2><span>{cx.type} · {cx.range}</span></div>
                  <ol className={s.steps} aria-label={t('hrv.steps')}>
                    {cycleStepView(cx.steps).map((st) => (
                      <li key={st.n}>
                        <span className={s.row}>
                          <span aria-hidden="true" className={`${s.dot} ${st.state === 'done' ? s.dotDone : st.state === 'cur' ? s.dotCur : ''}`}>{st.n}</span>
                          <b>{st.k}</b><span className={s.prog}>{st.prog}</span>
                        </span>
                        <span className={s.bar6} aria-hidden="true"><span className={st.state === 'done' ? s.fDone : s.fCur} style={{ width: `${st.pct}%` }} /></span>
                        <span className={s.sub}>{st.when}</span>
                      </li>
                    ))}
                  </ol>
                  {cx.status === 'open' && (
                    <div className={s.remind}>
                      <span className={s.remindK}><IconBell size={16} stroke={1.8} aria-hidden="true" />{t('hrv.remind')}</span>
                      {(['d7', 'd3', 'd1', 'hei', 'mail'] as const).map((k) => (
                        <Pill key={k} on={rmd[k]} onClick={() => setRmd({ ...rmd, [k]: !rmd[k] })}>{t(`hrv.${k === 'd7' ? 'rd7' : k === 'd3' ? 'rd3' : k === 'd1' ? 'rd1' : k === 'hei' ? 'rdHei' : 'rdMail'}`)}</Pill>
                      ))}
                      {notDone > 0 && <Button size="sm" onClick={remindAll}>{t('hrv.remindAll', { n: notDone })}</Button>}
                    </div>
                  )}
                </section>
                <section className={s.card} aria-label={t('hrv.people')}>
                  <div role="group" aria-label={t('hrv.filterSt')} className={s.filters}>
                    {PEOPLE_FILTERS.map((f) => (
                      <Pill key={f} big on={pf === f} onClick={() => setPf(f)}>{`${t(`hrv.f${f[0]!.toUpperCase()}${f.slice(1)}`)} · ${people.filter((p) => matchFilter(p, f)).length}`}</Pill>
                    ))}
                  </div>
                  <div className={s.pGrid}>
                    <div className={s.th}>{t('hrv.cStaff')}</div><div className={s.th}>{t('hrv.cMgr')}</div><div className={s.th}>{t('hrv.cSelf')}</div><div className={s.th}>{t('hrv.cMgr2')}</div>
                    <div className={`${s.th} ${s.right}`} title={t('hrv.cKpiTip')}>KPI</div><div />
                    {people.filter((p) => matchFilter(p, pf)).map((p) => (
                      <PersonRow key={p.user} p={p} open={cx.status === 'open'} label={progLabel} onOpen={() => { setRvUser(p.user); setRvSc({}); }} onRemind={() => remindOne(p)} remindAria={t('hrv.remindAria', { u: p.user })} remindK={t('hrv.remindOne')} />
                    ))}
                  </div>
                  {!people.some((p) => matchFilter(p, pf)) && <div className={s.noMatch}>{t('hrv.noMatch')}</div>}
                </section>
              </div>
            </div>
          )}

          {tab === 'okr' && (
            <>
              <div className={s.okBar}>
                <div role="group" aria-label={t('hrv.lvAria')} className={s.filters}>
                  {([['all', t('hrv.lvAll')], ['Công ty', t('hrv.lvCo')], ['Phòng', t('hrv.lvDept')]] as const).map(([k, lb]) => <Pill key={k} on={okLv === k} onClick={() => setOkLv(k)}>{lb}</Pill>)}
                </div>
                <span className={s.mute}>{t('hrv.okrCap')}</span>
                <button type="button" className={s.link} onClick={() => void navigate({ to: '/mee/hr/kpi' })}>{t('hrv.okrKpi')}</button>
              </div>
              {objs.map((o) => {
                const avg = objectiveAvg(o, krOv);
                return (
                  <section key={o.id} className={s.card} aria-label={o.name}>
                    <div className={s.objHd}>
                      <span className={`${s.chip} ${TONE[o.level === 'Công ty' ? 'pri' : 'mute']}`}>{o.level}</span>
                      <h2>{o.name}</h2>
                      <span className={s.sub}>{o.owner}</span>
                      <span className={s.bar} style={{ width: 120 }} aria-hidden="true"><span style={{ width: `${avg}%` }} /></span>
                      <b className={s.objPct}>{avg}%</b>
                    </div>
                    {o.krs.map(([id, k, own, v0, goal, u, inv]) => {
                      const v = krOv[id] ?? v0;
                      const pr = krProgress(v, goal, inv);
                      return (
                        <div key={id} className={s.krRow}>
                          <div className={s.krK}>{k}</div>
                          <div className={s.sub}>{own}</div>
                          <div className={s.krV}><span>{`${v}${u} / ${goal}${u}`}</span><span className={s.bar5} aria-hidden="true"><span className={TONE[pr.conf]} style={{ width: `${pr.pct}%` }} /></span></div>
                          <span className={`${s.chip} ${TONE[pr.conf]}`}>{t(pr.conf === 'ok' ? 'hrv.cOk' : pr.conf === 'warn' ? 'hrv.cWarn' : 'hrv.cErr')}</span>
                          <div className={s.krAct}>
                            <button type="button" className={s.sq} aria-label={t('hrv.dec', { k })} onClick={() => setKrOv({ ...krOv, [id]: Math.max(0, v - 1) })}>−</button>
                            <button type="button" className={s.sq} aria-label={t('hrv.inc', { k })} onClick={() => setKrOv({ ...krOv, [id]: v + 1 })}>+</button>
                            <button type="button" className={s.act} onClick={() => say(t('hrv.tCheckin', { k, v: `${v}${u}` }))}>{t('hrv.checkin')}</button>
                          </div>
                        </div>
                      );
                    })}
                  </section>
                );
              })}
            </>
          )}

          {tab === 'comp' && (
            <div className={s.compGrid}>
              <section className={s.card} aria-label={t('hrv.tComp')}>
                <div className={s.okBar}>
                  <div role="group" aria-label={t('hrv.pos')} className={s.filters}>{POSITIONS.map((p) => <Pill key={p} on={pos === p} onClick={() => setPos(p)}>{p}</Pill>)}</div>
                  <span className={s.mute} style={{ marginLeft: 'auto' }}>{cfEdit ? t('hrv.editing') : t('hrv.capLevel')}</span>
                </div>
                <div role="grid" aria-label={t('hrv.grid')} className={s.cfGrid}>
                  <div className={s.th}>{t('hrv.cap')}</div>
                  {LEVELS.map((l) => <div key={l} className={`${s.th} ${s.ctr}`}>{l}</div>)}
                  {FRAME.map(([k, lv], ri) => (
                    <FrameRow key={k} name={k} lv={lv} ri={ri} pos={pos} ov={cfOv} edit={cfEdit} aria={(l, v) => t('hrv.cellAria', { k, l, v }) + (cfEdit ? t('hrv.cellEdit') : '')} onPick={(key, v) => setCfOv({ ...cfOv, [key]: nextLevel(v) })} />
                  ))}
                </div>
              </section>
              <aside className={s.card} aria-label={t('hrv.gapHd')}>
                <h2 className={s.gapHd}>{t('hrv.gapHd')}</h2>
                {GAPS.map(([k, n, course]) => (
                  <div key={k} className={s.gap}>
                    <div><b>{k}</b><div className={s.sub}>{t('hrv.gapUnder', { n })} · {t('hrv.gapCourse', { c: course })}</div></div>
                    <button type="button" className={s.act} onClick={() => say(t('hrv.tAssign', { c: course, n }))}>{t('hrv.assign')}</button>
                  </div>
                ))}
                <button type="button" className={s.link} onClick={() => void navigate({ to: '/edu' })}>{t('hrv.edu')}</button>
              </aside>
            </div>
          )}
        </StateView>
      </div>

      <Drawer
        open={!!rvP}
        onOpenChange={(o) => !o && closeRv()}
        title={t('hrv.rvTitle', { u: rvP?.user ?? '' })}
        description={rvP ? `${rvP.name} · ${rvP.seat} · ${cx.name}` : undefined}
        footer={
          <>
            <Button onClick={() => { say(t('hrv.tDraft', { u: rvP?.user ?? '' })); closeRv(); }}>{t('hrv.rvDraft')}</Button>
            <Button variant="primary" icon={<IconSend size={16} aria-hidden="true" />} onClick={sendRv}>{t('hrv.rvSend')}</Button>
          </>
        }
      >
        {rvP && rv && (
          <div className={s.rv}>
            <div className={s.rvKpi} title={t('hrv.rvKpiTip')}><span>{t('hrv.rvKpi')}</span><b>{rvP.kpi ? `${rvP.kpi} / 100` : t('hrv.rvKpiNone')}</b></div>
            <div>
              <div className={s.rvGrid}><span className={s.th}>{t('hrv.cap')}</span><span className={`${s.th} ${s.ctr}`}>{t('hrv.rvSelf')}</span><span className={`${s.th} ${s.ctr}`}>{t('hrv.cMgr')}</span></div>
              {COMPS.map(([k, sub], i) => (
                <div key={k} className={`${s.rvGrid} ${s.rvRow}`}>
                  <div><div className={s.krK}>{k}</div><div className={s.sub}>{sub}</div></div>
                  <div className={`${s.ctr} ${s.krK}`}>{rvP.self === 2 ? SELF_SCORES[i] : '—'}</div>
                  <div role="radiogroup" aria-label={k} className={s.scores}>
                    {[1, 2, 3, 4, 5].map((v) => (
                      <button key={v} type="button" role="radio" aria-checked={rv.sc[i] === v} aria-label={t('hrv.rvScore', { k, v })} className={`${s.pill} ${s.score} ${rv.sc[i] === v ? s.pillOn : ''}`}
                        onClick={() => setRvSc({ ...Object.fromEntries(rv.sc.map((x, j) => [j, x])), [i]: v })}>{v}</button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className={s.rvTot}>
              <div><b>{t('hrv.rvTotal', { n: rv.done ? rv.total : '—' })}</b><div className={s.sub}>{t('hrv.rvFormula')}</div></div>
              <span className={`${s.grade} ${TONE[rv.grade === 'A' ? 'ok' : rv.grade === 'B' ? 'pri' : rv.grade === '—' ? 'mute' : 'warn']}`}>{t('hrv.rvGrade', { g: rv.grade })}</span>
            </div>
            <Textarea label={t('hrv.rvNote')} rows={3} value={rvNote} onChange={(e) => setRvNote(e.target.value)} placeholder={t('hrv.rvNotePh')} />
          </div>
        )}
      </Drawer>

      <Modal
        open={!!nf}
        onOpenChange={(o) => !o && setNf(null)}
        title={nf?.kind === 'cyc' ? t('hrv.nfCyc') : t('hrv.nfOkr')}
        footer={<><Button variant="ghost" onClick={() => setNf(null)}>{t('hrv.nfCancel')}</Button><Button variant="primary" onClick={submitNf}>{t('hrv.nfCreate')}</Button></>}
      >
        {nf && (
          <div className={s.nf}>
            <label>
              <div className={s.nfK}>{t('hrv.nfName')} <span className={s.req}>*</span></div>
              <input aria-label={t('hrv.nfName')} aria-invalid={nf.tried && !nf.name.trim()} className={s.nfIn} value={nf.name} placeholder={t('hrv.nfNamePh')} onChange={(e) => setNf({ ...nf, name: e.target.value })} />
            </label>
            {nf.kind === 'cyc' ? (
              <>
                <div><div className={s.nfK}>{t('hrv.nfType')}</div><div className={s.filters}>{CYCLE_TYPES.map((x) => <Pill key={x} big on={nf.ty === x} onClick={() => setNf({ ...nf, ty: x })}>{x}</Pill>)}</div></div>
                <div><div className={s.nfK}>{t('hrv.nfWho')}</div><div className={s.filters}>{['Toàn công ty · 36', 'Phòng OPS · 9', 'Phòng CSD · 8', 'Chọn người…'].map((x, i) => <Pill key={x} big on={nf.who === i} onClick={() => setNf({ ...nf, who: i })}>{x}</Pill>)}</div></div>
                <div className={s.mute}>{t('hrv.nfCalNote')}</div>
              </>
            ) : (
              <>
                <div><div className={s.nfK}>{t('hrv.nfLevel')}</div><div className={s.filters}>{['Công ty', 'Phòng HR', 'Phòng OPS', 'Phòng Kinh doanh'].map((x) => <Pill key={x} big on={nf.lv === x} onClick={() => setNf({ ...nf, lv: x })}>{x}</Pill>)}</div></div>
                <div className={s.mute}>{t('hrv.nfOkrNote')}</div>
              </>
            )}
          </div>
        )}
      </Modal>
    </Page>
  );
}

function PersonRow({ p, open, label, onOpen, onRemind, remindAria, remindK }: { p: Person; open: boolean; label: (n: number) => string; onOpen: () => void; onRemind: () => void; remindAria: string; remindK: string }) {
  const tone = (n: number) => TONE[n === 2 ? 'ok' : n === 1 ? 'warn' : 'mute'];
  const late = p.self < 2 || p.mgrDone < 2;
  return (
    <>
      <button type="button" className={s.pName} onClick={onOpen}>
        <span aria-hidden="true" className={s.avatar}>{initials(p.user)}</span>
        <span><b>{p.name}</b><span className={s.sub}>{p.user} · {p.seat}</span></span>
      </button>
      <div className={`${s.cell} ${s.tx2}`}>{p.mgr}</div>
      <div className={s.cell}><span className={`${s.chip} ${tone(p.self)}`}>{label(p.self)}</span></div>
      <div className={s.cell}><span className={`${s.chip} ${tone(p.mgrDone)}`}>{label(p.mgrDone)}</span></div>
      <div className={`${s.cell} ${s.right} ${s.bold}`}>{p.kpi ? p.kpi : '—'}</div>
      <div className={`${s.cell} ${s.right}`}>{late && open && <button type="button" className={s.act} aria-label={remindAria} onClick={onRemind}>{remindK}</button>}</div>
    </>
  );
}

function FrameRow({ name, lv, ri, pos, ov, edit, aria, onPick }: { name: string; lv: readonly number[]; ri: number; pos: string; ov: Record<string, number>; edit: boolean; aria: (l: string, v: number) => string; onPick: (key: string, v: number) => void }) {
  return (
    <>
      <div className={s.cfName}>{name}</div>
      {lv.map((v0, ci) => {
        const key = `${pos}:${ri}:${ci}`;
        const v = ov[key] ?? v0;
        const a = aria(LEVELS[ci]!, v);
        return (
          <button key={key} type="button" role="gridcell" disabled={!edit} aria-label={a} title={a} className={s.cfCell} onClick={() => onPick(key, v)}>
            {[1, 2, 3, 4, 5].map((d) => <span key={d} aria-hidden="true" className={`${s.cfDot} ${d <= v ? s.cfOn : ''}`} />)}
          </button>
        );
      })}
    </>
  );
}

